// src/pages/PaymentComplete.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

export default function PaymentComplete() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pi, setPi] = useState(null);
  const [error, setError] = useState(null);
  const [redirectStatus, setRedirectStatus] = useState(null);

  // Parse query params from Stripe redirect
  const { clientSecret } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      clientSecret: params.get("payment_intent_client_secret"),
      redirectStatus: params.get("redirect_status"),
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setRedirectStatus(params.get("redirect_status"));
  }, []);

  // Retrieve PaymentIntent details from Stripe
  useEffect(() => {
    (async () => {
      try {
        if (!clientSecret) {
          setError("Missing payment_intent_client_secret in the URL.");
          setLoading(false);
          return;
        }
        const stripe = await stripePromise;
        const { paymentIntent, error: piError } = await stripe.retrievePaymentIntent(clientSecret);
        if (piError) {
          setError(piError.message || "Could not retrieve the payment result.");
          setLoading(false);
          return;
        }
        setPi(paymentIntent || null);
      } catch (e) {
        setError(e?.message || "Something went wrong retrieving your payment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [clientSecret]);

  const status = pi?.status || redirectStatus || "unknown";
  const isSucceeded = status === "succeeded";
  const isProcessing = status === "processing" || status === "requires_action";
  const isFailed =
    status === "requires_payment_method" ||
    status === "canceled" ||
    status === "requires_capture" ||
    status === "requires_confirmation" ||
    status === "unknown";

  const amountUSD = useMemo(() => {
    const cents = typeof pi?.amount === "number" ? pi.amount : null;
    const currency = (pi?.currency || "usd").toUpperCase();
    return cents != null ? `$${(cents / 100).toFixed(2)} ${currency}` : "—";
  }, [pi]);

  // Metadata passed from backend
  const plan = prettyPlan(pi?.metadata?.plan) || "—";
  const qty = pi?.metadata?.qty || "—";
  const receiptEmail = pi?.receipt_email || "—";

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {loading ? (
          <div style={styles.center}>
            <div style={styles.spinner} />
            <h2 style={styles.title}>Checking your payment…</h2>
            <p style={styles.muted}>Please wait a moment while we confirm the result.</p>
          </div>
        ) : error ? (
          <>
            <Badge tone="danger">Payment Error</Badge>
            <h2 style={styles.title}>We couldn’t verify your payment</h2>
            <p style={styles.muted}>{error}</p>
            <ActionsFailed navigate={navigate} />
          </>
        ) : isSucceeded ? (
          <>
            <Badge tone="success">Payment Successful</Badge>
            <h2 style={styles.title}>🎉 Thank you! Your purchase is complete.</h2>

            <div style={styles.section}>
              <SectionRow label="Status" value="Succeeded" />
              <SectionRow label="Amount" value={amountUSD} />
              <SectionRow label="Plan" value={plan} />
              <SectionRow label="Quantity" value={qty} />
              <SectionRow label="Billing email" value={receiptEmail} />
              {pi?.livemode === false && (
                <div style={{ marginTop: 8 }}>
                  <Badge tone="warning">Test Mode</Badge>
                </div>
              )}
            </div>

            <p style={{ ...styles.muted, marginTop: 16 }}>
              Your account is being updated now. You can start using your plan immediately.
            </p>

            <div style={styles.actions}>
              <Link to="/dashboard" style={{ ...styles.button, ...styles.buttonPrimary }}>
                Go to Dashboard
              </Link>
              <Link to="/billing" style={{ ...styles.button, ...styles.buttonGhost }}>
                View Billing
              </Link>
            </div>

            <p style={{ ...styles.help, marginTop: 14 }}>
              Need help? <Link to="/contact">Contact support</Link>.
            </p>
          </>
        ) : isProcessing ? (
          <>
            <Badge tone="info">Processing</Badge>
            <h2 style={styles.title}>We’re finalizing your payment…</h2>
            <p style={styles.muted}>This can take a few moments. You’ll see your plan update shortly.</p>
            <div style={styles.actions}>
              <Link to="/dashboard" style={{ ...styles.button, ...styles.buttonPrimary }}>
                Go to Dashboard
              </Link>
              <Link to="/billing" style={{ ...styles.button, ...styles.buttonGhost }}>
                View Billing
              </Link>
            </div>
          </>
        ) : isFailed ? (
          <>
            <Badge tone="danger">Payment Failed</Badge>
            <h2 style={styles.title}>Payment was not completed</h2>
            <p style={styles.muted}>
              Your card was declined or additional steps are required. Try again with a different
              method or contact support if the issue persists.
            </p>

            <div style={styles.section}>
              <SectionRow label="Status" value={humanizeStatus(status)} />
              {pi?.last_payment_error?.message && (
                <SectionRow label="Reason" value={pi.last_payment_error.message} />
              )}
              <SectionRow label="Amount" value={amountUSD} />
              <SectionRow label="Plan" value={plan} />
            </div>

            <ActionsFailed navigate={navigate} />
          </>
        ) : (
          <>
            <Badge tone="warning">Unknown</Badge>
            <h2 style={styles.title}>We couldn’t determine the result</h2>
            <p style={styles.muted}>
              If you completed payment, your account will update shortly. Otherwise, try again.
            </p>
            <div style={styles.actions}>
              <Link to="/dashboard" style={{ ...styles.button, ...styles.buttonPrimary }}>
                Go to Dashboard
              </Link>
              <Link to="/billing" style={{ ...styles.button, ...styles.buttonGhost }}>
                View Billing
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- UI helpers ---------- */

function Badge({ tone = "info", children }) {
  const stylesMap = {
    success: { background: "#E7F6EC", color: "#0F8A3D", border: "1px solid #B6E3C1" },
    danger: { background: "#FDE8E8", color: "#A30000", border: "1px solid #F5C2C0" },
    warning: { background: "#FFF4E5", color: "#8B5E00", border: "1px solid #FFE0B2" },
    info: { background: "#E8F1FD", color: "#0B63C5", border: "1px solid #CDE0FB" },
  };
  return <span style={{ ...badgeStyles.base, ...stylesMap[tone] }}>{children}</span>;
}

function SectionRow({ label, value }) {
  return (
    <div style={styles.row}>
      <div style={styles.rowLabel}>{label}</div>
      <div style={styles.rowValue}>{value}</div>
    </div>
  );
}

function ActionsFailed({ navigate }) {
  return (
    <div style={styles.actions}>
      <button onClick={() => navigate(-1)} style={{ ...styles.button, ...styles.buttonPrimary }}>
        Try Again
      </button>
      <Link to="/billing" style={{ ...styles.button, ...styles.buttonGhost }}>
        Go to Billing
      </Link>
      <Link to="/contact" style={{ ...styles.button, ...styles.buttonText }}>
        Contact Support
      </Link>
    </div>
  );
}

function prettyPlan(plan) {
  switch (plan) {
    case "session":
      return "Session Pack";
    case "monthly":
      return "Monthly Plan";
    case "smart_alert":
      return "Smart Alert Plan";
    case "free":
      return "Free Plan";
    default:
      return plan || "—";
  }
}

function humanizeStatus(s) {
  const map = {
    succeeded: "Succeeded",
    processing: "Processing",
    requires_action: "Requires Action",
    requires_payment_method: "Requires Payment Method",
    requires_confirmation: "Requires Confirmation",
    canceled: "Canceled",
    unknown: "Unknown",
  };
  return map[s] || s;
}

/* ---------- Inline styles ---------- */

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f7fafc 0%, #ffffff 100%)",
    padding: "40px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 680,
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
    padding: 24,
    border: "1px solid rgba(0,0,0,0.06)",
  },
  title: { margin: "8px 0 8px", fontSize: 24, fontWeight: 700, color: "#111827" },
  muted: { color: "#6b7280", lineHeight: 1.5, marginTop: 6 },
  section: {
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    background: "#fafafa",
    border: "1px solid #eee",
  },
  row: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    padding: "10px 0",
    borderBottom: "1px dashed #e5e7eb",
  },
  rowLabel: { width: 160, color: "#6b7280", fontWeight: 600 },
  rowValue: { color: "#111827" },
  actions: { display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" },
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 16px",
    borderRadius: 10,
    border: "1px solid transparent",
    textDecoration: "none",
    fontWeight: 600,
  },
  buttonPrimary: { background: "#0EA5E9", color: "#fff" },
  buttonGhost: { background: "#fff", borderColor: "#e5e7eb", color: "#111827" },
  buttonText: { background: "transparent", color: "#0B63C5" },
  center: { textAlign: "center" },
  help: { fontSize: 14, color: "#6b7280" },
};

const badgeStyles = {
  base: {
    display: "inline-block",
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 999,
    marginBottom: 8,
  },
};
